import {  forwardRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Slide, { SlideProps } from '@mui/material/Slide';
import styled from 'styled-components/macro';

import { State } from '../reducer';
import { Message, MessagesState, namespace } from './actions';
import { useActions } from '../Actions';

const Alert = styled(MuiAlert)`&&{
    ${({ theme, variant }) => variant === 'outlined' ? {
        backgroundColor: theme.palette.background.paper,
    } : undefined};
}`;

const TransitionComponent = forwardRef<unknown, SlideProps>((props, ref) => <Slide ref={ref} direction="down" {...props} />);

const autoHideDuration = 2000;

const Messages = () => {
    // The react snackbar can only show one message at a time so we queue them in the state
    const { t } = useTranslation();
    const { removeVisibleMessage } = useActions(namespace);
    const { messages } = useSelector<State, MessagesState>(
        (s) => s[namespace]
    );
    const [message] = messages;
    const [open, setOpen] = useState(!!message);

    useEffect(() => {
        setOpen(!!message);
    }, [message]);


    const onSnackClose = useCallback((_, reason: string) => {
        if (reason === 'timeout') setOpen(false);
    }, []);

    useLayoutEffect(() => {
        // clear when unmounting this component
        return () => {
            removeVisibleMessage();
        };
    }, [removeVisibleMessage]);

    return (
        <Snackbar
            key={`message-${message?.id}`}
            open={open}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            autoHideDuration={autoHideDuration}
            onClose={onSnackClose}
            TransitionComponent={TransitionComponent}
            TransitionProps={{
                onExited: removeVisibleMessage,
            }}>
            <Alert
                elevation={2}
                severity={message?.severity}
                onClose={() => setOpen(false)}
                closeText={t('messages.close')}
                variant="outlined"
            >
                {message && message.text}
            </Alert>
        </Snackbar>
    );
};

export default Messages;
